// Screenshots for the assistant. They stay in the browser: sent to the model with one message, never uploaded or saved,
// because a screenshot of the platform can show beneficiary data

export const MAX_IMAGES = 4
const MAX_INPUT_BYTES = 20 * 1024 * 1024
// Long enough to read UI text, small enough to keep requests fast
const MAX_SIDE = 1600

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error("That file isn't an image the browser can read."))
    img.src = url
  })
}

// A file as a data URL, scaled down to MAX_SIDE. PNG stays PNG so UI text stays sharp; photos become JPEG
export async function readImage(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) throw new Error(`${file.name || "That file"} isn't an image.`)
  if (file.size > MAX_INPUT_BYTES) throw new Error(`${file.name || "That image"} is over 20 MB.`)
  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight))
    const canvas = document.createElement("canvas")
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Couldn't read the image.")
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    return file.type === "image/png" ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.85)
  } finally {
    URL.revokeObjectURL(url)
  }
}

// What a saved chat keeps in place of the screenshots themselves
export const attachmentNote = (count: number) =>
  count ? `[${count} screenshot${count === 1 ? "" : "s"} attached, not saved]` : ""
